import { client } from '@/src/api/client.gen';

/**
 * A wrapper around the OpenAPI client that behaves exactly like the standard fetch API.
 * Automatically handles base URL formatting and Clerk authentication headers.
 */
export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  let targetUrl = url;
  if (!targetUrl.startsWith('http://') && !targetUrl.startsWith('https://')) {
    if (targetUrl.startsWith('api/v1/')) {
      targetUrl = `/${targetUrl}`;
    } else if (!targetUrl.startsWith('/api/v1/')) {
      if (targetUrl.startsWith('/api/')) {
        targetUrl = targetUrl.replace(/^\/api\//, '');
      }
      targetUrl = targetUrl.startsWith('/') ? `/api/v1${targetUrl}` : `/api/v1/${targetUrl}`;
    }
  }
  targetUrl = targetUrl.replace(/\/api\/v1\/api\/v1\//g, '/api/v1/');

  const method = (options.method || 'GET').toUpperCase() as any;
  const headers = new Headers(options.headers);

  // Automatically attach Clerk Authorization token if user is signed in and no auth header passed
  if (!headers.has('Authorization') && typeof window !== 'undefined') {
    const clerk = (window as any).Clerk;
    if (clerk) {
      if (!clerk.loaded) {
        let waited = 0;
        while (!clerk.loaded && waited < 3000) {
          await new Promise((resolve) => setTimeout(resolve, 50));
          waited += 50;
        }
      }
      if (clerk.session) {
        try {
          const token = await clerk.session.getToken();
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          }
        } catch {
          /* session unavailable */
        }
      }
    }
  }

  const headerObj: Record<string, string | null> = {};
  headers.forEach((value, key) => {
    headerObj[key] = value;
  });

  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const isBinary =
    (typeof Blob !== 'undefined' && options.body instanceof Blob) ||
    (typeof ArrayBuffer !== 'undefined' &&
      (options.body instanceof ArrayBuffer || ArrayBuffer.isView(options.body)));

  let parsedBody = options.body;
  if (!isFormData && !isBinary) {
    if (typeof options.body === 'string') {
      const isJsonHeader = (headerObj['Content-Type'] || headerObj['content-type'])?.includes('application/json');
      const trimmed = options.body.trim();
      if (
        isJsonHeader ||
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          parsedBody = JSON.parse(options.body);
        } catch {}
      }
    }
  }

  const requestHeaders: Record<string, string | null> = { ...headerObj };
  if (isFormData) {
    // Setting Content-Type to null instructs Hey-API mergeHeaders to delete Content-Type,
    // allowing the browser to set multipart/form-data with the correct boundary.
    delete requestHeaders['content-type'];
    delete requestHeaders['Content-Type'];
    requestHeaders['Content-Type'] = null;
  } else if (!isBinary && parsedBody !== undefined) {
    const hasContentType = Object.keys(requestHeaders).some(
      (k) => k.toLowerCase() === 'content-type' && requestHeaders[k]
    );
    if (!hasContentType) {
      requestHeaders['Content-Type'] = 'application/json';
    }
  }

  const jsonSerializer = (body: unknown) => {
    if (typeof body === 'string') return body;
    return JSON.stringify(body, (_key, value) => (typeof value === 'bigint' ? value.toString() : value));
  };

  const res = await client.request({
    method,
    url: targetUrl,
    body: parsedBody,
    headers: requestHeaders as any,
    bodySerializer: isFormData || isBinary ? null : (jsonSerializer as any),
    signal: options.signal,
  });

  const rawRes = res as any;
  const isOk = rawRes.response ? rawRes.response.ok : !rawRes.error;
  const statusCode = rawRes.response?.status ?? (isOk ? 200 : 400);
  const statusText = rawRes.response?.statusText ?? (isOk ? 'OK' : 'Error');

  const payload = rawRes.data !== undefined ? rawRes.data : rawRes.error;
  const bodyText = typeof payload === 'string' ? payload : (payload !== undefined ? JSON.stringify(payload) : '');

  return new Response(bodyText, {
    status: statusCode,
    statusText,
    headers: rawRes.response?.headers,
  });
}
export default apiFetch;
